/**
 * Ready Boards — the $12/yr Starter tier as a PUBLIC marketing page in its
 * designed Coming Soon state (replaces the auth-gated ComingSoonPage stub;
 * contest brief, 2026-07-11).
 *
 * Facts from CANON block 2 / blueprint Part M+R2-I, quoted-not-paraphrased
 * where they are law: $12/year, boards graduate 6 → 12 → 18 words, fixed
 * layouts, families add their OWN voice recordings and photos, 3¢/day.
 * Visible, honest, NON-launchable: no purchase affordance exists here.
 */
import { Link } from "react-router-dom";
import { Blocks, Mic, TrendingUp, Clock } from "lucide-react";
import { useSEO } from "@/lib/seo";

const STAGES = [
  { words: "6", who: "First words — yes, no, more, help, eat, drink." },
  { words: "12", who: "Growing — feelings, people, and favorite things join." },
  { words: "18", who: "Confident — full daily routines on one board." },
];

export default function ReadyBoards() {
  useSEO({
    title: "Ready Boards — Starter Graduated Boards",
    description:
      "Premade communication boards that graduate 6 → 12 → 18 words as your " +
      "child grows — with your own photos and voice on the tiles. $12 a " +
      "year. Coming with paid plans.",
    path: "/ready-boards",
  });

  return (
    <div className="min-h-screen bg-[color:var(--cream)]" data-testid="ready-boards-page">
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-8 text-center">
        <p className="display text-xs font-bold uppercase tracking-[0.24em] text-[color:var(--coral-deep)] mb-3">
          Starter — Graduated Boards
        </p>
        <h1 className="display text-3xl sm:text-5xl font-extrabold leading-tight mb-4">
          A first voice for <span className="text-[color:var(--coral)]">3¢ a day.</span>
        </h1>
        <p className="text-base sm:text-lg text-[color:var(--ink)]/80 max-w-2xl mx-auto leading-relaxed">
          For babies, toddlers, and small kids before their first big board:
          ready-made boards that <strong>graduate 6 → 12 → 18 words</strong> as
          your child grows. $12 a year — a third the price of a plastic picture
          board that never changes.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-8" aria-label="Graduation stages">
        <div className="grid sm:grid-cols-3 gap-4">
          {STAGES.map((s) => (
            <div key={s.words} className="brutal-card bg-white p-6 text-center">
              <div className="display text-4xl font-extrabold text-[color:var(--coral)]">{s.words}</div>
              <div className="display text-xs font-bold uppercase tracking-[0.18em] mb-2">words</div>
              <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">{s.who}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-[color:var(--ink-soft)] mt-4 flex items-center justify-center gap-2">
          <TrendingUp size={16} aria-hidden />
          Graduating up keeps every tile your child already knows — it adds, never rebuilds.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-8">
        <div className="brutal-card bg-[color:var(--paper)] p-6">
          <h2 className="display text-xl font-extrabold mb-3 flex items-center gap-2">
            <Mic size={20} aria-hidden /> Fixed layout, your family&rsquo;s soul
          </h2>
          <p className="text-sm leading-relaxed mb-2">
            Starter boards keep a fixed layout — that&rsquo;s what makes them
            dependable for little hands. But every tile takes{" "}
            <strong>your own photo and your own voice recording</strong>.
            &ldquo;Mama&rdquo; is your face and your voice, from day one.
          </p>
          <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
            When your child hits the 30-word era, that&rsquo;s the graduation
            moment — <Link to="/pricing" className="underline font-semibold">Membership</Link>{" "}
            unlocks unlimited boards you build and grow yourself.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16">
        <div className="brutal-card bg-[color:var(--gold)] p-6 text-center" data-testid="ready-boards-coming-soon">
          <h2 className="display text-lg font-extrabold mb-2 flex items-center justify-center gap-2">
            <Clock size={18} aria-hidden /> Coming with paid plans
          </h2>
          <p className="text-sm leading-relaxed mb-4 max-w-xl mx-auto">
            Ready Boards launch when checkout opens (after my business
            registration completes). Every board ships only after a human has
            reviewed every image on it. Join the beta and you&rsquo;ll hear the
            day they arrive.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/#beta" className="pill-btn primary" data-testid="ready-boards-beta">
              <Blocks size={16} aria-hidden /> Join the beta
            </Link>
            <Link to="/demo" className="pill-btn">Try the live demo</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
